import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OrganizationsOnboarding } from '@/features/organizations/components/organizations-onboarding';
import { OrganizationsPageShell } from '@/features/organizations/components/organizations-page-shell';
import { useOrganizations } from '@/features/organizations/hooks/useOrganizations';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';
import { trackAmplitude } from '@/lib/amplitude';
import { requireAuthenticatedRoute } from '@/lib/router-guards';

export const Route = createFileRoute('/_app/organizations')({
    beforeLoad: requireAuthenticatedRoute,
    component: OrganizationsPage
});

function OrganizationsPage() {
    const { t } = useTranslation();
    const organizationsQuery = useOrganizations();

    useEffect(() => {
        trackAmplitude('Viewed Organizations Page', { prompt_version: 'BA400.4' });
    }, []);

    if (organizationsQuery.isPending) {
        return (
            <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
                <Card>
                    <CardHeader>
                        <CardTitle>{t('organizations.loading.title')}</CardTitle>
                        <CardDescription>{t('organizations.loading.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className='h-24 animate-pulse rounded-xl bg-muted' />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (organizationsQuery.isError) {
        return (
            <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
                <Card>
                    <CardHeader>
                        <CardTitle>{t('organizations.error.title')}</CardTitle>
                        <CardDescription>{t('organizations.error.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className='text-sm text-destructive'>
                            {resolveApiErrorMessage(organizationsQuery.error, t, {
                                fallbackKey: 'apiErrors.organizations.loadFailed',
                                statusFallbacks: apiErrorStatusFallbackPresets.organizationsLoad
                            })}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!organizationsQuery.data?.length) {
        return <OrganizationsOnboarding />;
    }

    return <OrganizationsPageShell />;
}
